import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { customersAPI, contractsAPI, companiesAPI, contractTypesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { tr, enUS, de } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Info,
  Users,
  FileText,
  FileCheck,
  UserX,
  UserPlus,
  Building2,
  Bookmark,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  ArrowUpDown,
  UploadCloud,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { cn } from '../lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import debounce from 'lodash.debounce';
import { useRef } from 'react';

function isValidPhoneNumber(value) {
  const v = String(value || '').trim();
  if (!v) return true;
  // Must start with + and country code; allow common phone characters; require at least 6 digits.
  if (!/^\+\d/.test(v)) return false;
  if (!/^\+\d[\d\s().-]*$/.test(v)) return false;
  const digits = (v.match(/\d/g) || []).length;
  return digits >= 6;
}

const getCustomerSchema = (t) => z
  .object({
    first_name: z.string().min(1, t('customers.firstNameRequired')),
    last_name: z.string().min(1, t('customers.lastNameRequired')),
    // These flags drive conditional required rules.
    no_email: z.boolean().default(false),
    no_phone: z.boolean().default(false),
    email: z.string().email(t('customers.invalidEmail')).optional().or(z.literal('')),
    phone: z
      .string()
      .optional()
      .refine((v) => isValidPhoneNumber(v), {
        message: t('customers.invalidPhone'),
      }),
    birth_date: z.string().min(1, t('customers.birthDateRequired')),
    address: z.string().min(1, t('customers.addressRequired')),
    city: z.string().optional(),
    zip_code: z
      .string()
      .min(1, t('customers.zipCodeRequired'))
      .refine((v) => /^\d+$/.test(String(v).trim()), {
        message: t('customers.zipCodeNumeric'),
      }),
  })
  .superRefine((val, ctx) => {
    if (!val.no_email && !String(val.email || '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['email'],
        message: t('customers.emailRequired'),
      });
    }
    if (!val.no_phone && !String(val.phone || '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['phone'],
        message: t('customers.phoneRequired'),
      });
    }
  });

/**
 * Kategori dağılımı kartı — şık, genişletilebilir. Bar YOK; her satır rütbe + isim +
 * sayı + yüzde pill'i. Sıfır sözleşmeli kalemler gizli. Varsayılan ilk `topN`, "Tümünü
 * göster" ile hepsi açılır. Aramadan bağımsız, tam veri. items: [{ id, name, count, inactive }]
 */
function DistributionCard({ icon: Icon, title, footnote, emptyText, formatMore, lessLabel, items, topN = 5 }) {
  const [expanded, setExpanded] = useState(false);
  const withData = items.filter((it) => it.count > 0).sort((a, b) => b.count - a.count);
  const total = withData.reduce((sum, it) => sum + it.count, 0);
  const visible = expanded ? withData : withData.slice(0, topN);
  const hidden = withData.length - topN;
  const nf = (n) => n.toLocaleString();
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <Card className="group dark:bg-[#333333] dark:border-zinc-800 transition-all duration-200 hover:shadow-lg hover:shadow-black/5 hover:-translate-y-0.5 hover:border-emerald-500/30 dark:hover:border-emerald-500/30">
      <CardContent className="p-4">
        {/* Başlık */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="rounded-md bg-gray-100 dark:bg-[#444444] p-1.5 shrink-0">
              <Icon className="w-4 h-4 text-muted-foreground dark:text-gray-300" />
            </div>
            <h3 className="text-sm font-semibold truncate dark:text-white">{title}</h3>
          </div>
          <span className="rounded-full bg-gray-100 dark:bg-[#444444] px-2 py-0.5 text-xs font-mono tabular-nums text-muted-foreground dark:text-gray-300 shrink-0">
            {nf(total)}
          </span>
        </div>

        {withData.length === 0 ? (
          <p className="text-sm text-muted-foreground dark:text-gray-500 py-6 text-center">{emptyText}</p>
        ) : (
          <>
            <ul className="space-y-0.5">
              {visible.map((it, i) => (
                <li
                  key={it.id}
                  className="dc-row flex items-center gap-3 rounded-lg px-2 -mx-2 py-2 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                  style={{ animation: 'dcFadeIn 0.28s ease-out both', animationDelay: `${i * 28}ms` }}
                >
                  <span
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-mono font-bold shrink-0 ring-1 ring-inset',
                      it.inactive
                        ? 'bg-gray-100 text-muted-foreground ring-transparent dark:bg-[#444444] dark:text-gray-500'
                        : i === 0
                        ? 'bg-amber-100 text-amber-700 ring-amber-500/20 dark:bg-amber-500/15 dark:text-amber-300'
                        : i === 1
                        ? 'bg-slate-200 text-slate-600 ring-slate-400/20 dark:bg-slate-400/15 dark:text-slate-200'
                        : i === 2
                        ? 'bg-orange-100 text-orange-700 ring-orange-500/20 dark:bg-orange-500/15 dark:text-orange-300'
                        : 'bg-gray-100 text-muted-foreground ring-transparent dark:bg-[#444444] dark:text-gray-400'
                    )}
                  >
                    {i + 1}
                  </span>
                  <span
                    className={cn(
                      'flex-1 min-w-0 truncate text-sm transition-colors',
                      it.inactive
                        ? 'text-muted-foreground line-through dark:text-gray-500'
                        : 'dark:text-gray-100 group-hover:text-zinc-900 dark:group-hover:text-white'
                    )}
                    title={it.name}
                  >
                    {it.name}
                  </span>
                  <span className="font-mono tabular-nums text-sm font-semibold text-zinc-800 dark:text-gray-100 shrink-0">
                    {nf(it.count)}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-mono font-medium min-w-[2.75rem] text-center transition-colors',
                      it.inactive
                        ? 'bg-gray-100 text-muted-foreground dark:bg-[#3a3a3a] dark:text-gray-500'
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                    )}
                  >
                    {pct(it.count)}%
                  </span>
                </li>
              ))}
            </ul>

            {withData.length > topN && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-transparent py-1.5 text-xs font-medium text-muted-foreground dark:text-gray-400 hover:bg-gray-100 hover:text-zinc-700 dark:hover:bg-[#3a3a3a] dark:hover:text-gray-200 hover:border-gray-200 dark:hover:border-white/10 transition-all active:scale-[0.98]"
                data-testid="distribution-toggle"
                aria-expanded={expanded}
              >
                {expanded ? lessLabel : formatMore(hidden)}
                <ChevronDown
                  className={cn('w-3.5 h-3.5 transition-transform duration-200', expanded && 'rotate-180')}
                />
              </button>
            )}
          </>
        )}

        <p className="mt-3 text-[11px] text-muted-foreground dark:text-gray-500">{footnote}</p>
      </CardContent>
    </Card>
  );
}

export default function Customers() {
  const { t, i18n } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Pagination (Contracts sayfası ile aynı davranış)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerContracts, setCustomerContracts] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [noEmail, setNoEmail] = useState(false);
  const [noPhone, setNoPhone] = useState(false);
  const [sort, setSort] = useState({ key: 'last_name', dir: 'asc' }); // default: A->Z by last name

  // Contract modals (reuse same UX as Contracts page)
  const [isContractDetailOpen, setIsContractDetailOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isContractEditOpen, setIsContractEditOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [customerStats, setCustomerStats] = useState(null);

  const formatDateDisplay = (value) => {
    const v = String(value || '').trim();
    if (!v) return '-';
    // If it's ISO (YYYY-MM-DD), show DD-MM-YYYY like the screenshot.
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    return v;
  };

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(getCustomerSchema(t)),
  });

  const getLocale = () => {
    switch (i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };
  const currentLocale = getLocale();
  const watchedBirthDate = watch('birth_date');

  useEffect(() => {
    fetchData(1, itemsPerPage, '');
    fetchStats();
    ensureContractMetaLoaded();
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const res = await customersAPI.getStats();
      setCustomerStats(res?.data || null);
    } catch (error) {
      // İstatistik kritik değil; hata olsa da sayfa çalışmaya devam etsin.
    }
  };

  const fetchData = async (page = 1, pageSize = 10, search = '') => {
    try {
      const skip = (page - 1) * pageSize;
      const res = await customersAPI.getPaged({
        search: (search || '').trim() ? (search || '').trim() : undefined,
        skip,
        limit: pageSize,
      });
      const items = res?.data?.items || [];
      const total = Number(res?.data?.total || 0);
      setCustomers(items);
      setTotalCustomers(total);

      // If we ended up on an empty page (e.g. after delete), jump back to first page.
      if (total > 0 && items.length === 0 && page > 1) {
        setCurrentPage(1);
        const res2 = await customersAPI.getPaged({
          search: (search || '').trim() ? (search || '').trim() : undefined,
          skip: 0,
          limit: pageSize,
        });
        setCustomers(res2?.data?.items || []);
        setTotalCustomers(Number(res2?.data?.total || 0));
      }
    } catch (error) {
      toast.error(t('customers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      try {
        const q = (query || '').trim();
        const res = await customersAPI.getPaged({
          search: q ? q : undefined,
          skip: 0,
          limit: itemsPerPage,
        });
        setCustomers(res?.data?.items || []);
        setTotalCustomers(Number(res?.data?.total || 0));
        setCurrentPage(1);
      } catch (error) {
        console.error(error);
      }
    }, 300),
    [itemsPerPage]
  );

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleOpenModal = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      const nextNoEmail = !customer.email;
      const nextNoPhone = !customer.phone;
      setNoEmail(nextNoEmail);
      setNoPhone(nextNoPhone);
      reset({
        first_name: customer.first_name,
        last_name: customer.last_name,
        no_email: nextNoEmail,
        no_phone: nextNoPhone,
        email: customer.email || '',
        phone: customer.phone || '',
        birth_date: customer.birth_date || '',
        address: customer.address || '',
        city: customer.city || '',
        zip_code: customer.zip_code || '',
      });
    } else {
      setEditingCustomer(null);
      setNoEmail(false);
      setNoPhone(false);
      reset({
        first_name: '',
        last_name: '',
        no_email: false,
        no_phone: false,
        email: '',
        phone: '',
        birth_date: '',
        address: '',
        city: '',
        zip_code: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
    setNoEmail(false);
    setNoPhone(false);
    reset();
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const noEmailSelected = data?.no_email === true;
      const noPhoneSelected = data?.no_phone === true;
      const payload = {
        ...data,
        email: noEmailSelected ? null : (data.email || null),
        phone: noPhoneSelected ? null : (data.phone || null),
        birth_date: data.birth_date || null,
        address: data.address || null,
        zip_code: data.zip_code || null,
      };

      // Do not store flags in DB
      delete payload.no_email;
      delete payload.no_phone;

      if (editingCustomer) {
        await customersAPI.update(editingCustomer.id, payload);
        toast.success(t('customers.updateSuccess'));
      } else {
        await customersAPI.create(payload);
        toast.success(t('customers.addSuccess'));
      }
      handleCloseModal();
      await fetchData(1, itemsPerPage, searchQuery);
      fetchStats();
      setCurrentPage(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!customerToDelete) return;

    try {
      const idToDelete = customerToDelete.id;
      setDeleteModalOpen(false);
      await customersAPI.delete(idToDelete);
      toast.success(t('customers.deleteSuccess'));
      await fetchData(currentPage, itemsPerPage, searchQuery);
      fetchStats();
    } catch (error) {
      toast.error(t('customers.deleteError'));
    } finally {
      setCustomerToDelete(null);
    }
  };

  const handleViewDetail = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await customersAPI.getContracts(customer.id);
      setCustomerContracts(response.data);
    } catch (error) {
      setCustomerContracts([]);
    }
    setIsDetailOpen(true);
  };

  const refreshCustomerContracts = async () => {
    if (!selectedCustomer?.id) return;
    try {
      const response = await customersAPI.getContracts(selectedCustomer.id);
      setCustomerContracts(response.data);
    } catch {
      // ignore
    }
  };

  const ensureContractMetaLoaded = async () => {
    if (companies.length > 0 && contractTypes.length > 0) return;
    try {
      const [companiesRes, typesRes] = await Promise.all([
        companiesAPI.getAll(),
        contractTypesAPI.getAll(),
      ]);
      setCompanies(companiesRes.data || []);
      setContractTypes(typesRes.data || []);
    } catch (e) {
      // non-blocking for modal open; dropdowns might be empty
      console.error(e);
    }
  };

  const handleViewContractDetail = async (contract) => {
    try {
      const response = await contractsAPI.getById(contract.id);
      setSelectedContract(response.data);
      setIsContractDetailOpen(true);
    } catch (error) {
      toast.error(t('contracts.detailLoadError', { defaultValue: 'Sözleşme detayları yüklenemedi' }));
    }
  };

  const handleEditContract = async (contract) => {
    try {
      await ensureContractMetaLoaded();
      const response = await contractsAPI.getById(contract.id);
      setEditingContract({ ...response.data });
      setSelectedFiles([]);
      setDragActive(false);
      setIsContractEditOpen(true);
    } catch (error) {
      toast.error(t('contracts.detailLoadError', { defaultValue: 'Sözleşme detayları yüklenemedi' }));
    }
  };

  const handleDeleteExistingContractFile = async (fileId) => {
    if (!editingContract) return;
    try {
      await contractsAPI.deleteFile(editingContract.id, fileId);
      toast.success(t('contracts.fileDeleted', { defaultValue: 'File deleted successfully' }));
      setEditingContract({
        ...editingContract,
        files: (editingContract.files || []).filter(f => f.id !== fileId)
      });
      await refreshCustomerContracts();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(t('contracts.deleteFileError', { defaultValue: 'Error deleting file' }));
    }
  };

  const handleContractFileChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleExtendContractDate = (months) => {
    if (!editingContract?.end_date) return;
    const date = new Date(editingContract.end_date);
    if (isNaN(date.getTime())) return;
    date.setMonth(date.getMonth() + months);
    const newDate = date.toISOString().split('T')[0];
    setEditingContract((prev) => ({
      ...prev,
      end_date: newDate,
      extended: true,
      extended_months: months,
    }));
  };

  const handleSaveContractChanges = async () => {
    if (!editingContract) return;
    try {
      const updateData = {
        contract_type_id: editingContract.contract_type_id,
        company_id: editingContract.company_id,
        start_date: editingContract.start_date,
        end_date: editingContract.end_date,
        order_number: editingContract.order_number,
        contract_number: editingContract.contract_number,
        description: editingContract.description,
        extended: editingContract.extended === true,
        extended_months: editingContract.extended_months ?? null,
      };

      await contractsAPI.update(editingContract.id, updateData);
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          await contractsAPI.uploadFile(editingContract.id, file);
        }
      }

      toast.success(t('contracts.updateSuccess', { defaultValue: 'Sözleşme güncellendi' }));
      setIsContractEditOpen(false);
      await refreshCustomerContracts();
      await fetchData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(t('contracts.updateError', { defaultValue: 'Sözleşme güncellenemedi' }));
    }
  };

  // Pagination calculations (Contracts sayfası ile aynı)
  const handleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
    setCurrentPage(1);
  };

  const getSortValue = (customer, key) => {
    switch (key) {
      case 'first_name':
        return (customer.first_name || '').toString();
      case 'last_name':
        return (customer.last_name || '').toString();
      case 'email':
        return (customer.email || '').toString();
      case 'phone':
        return (customer.phone || '').toString();
      case 'address':
        return (customer.address || '').toString();
      case 'zip_code':
        return (customer.zip_code || '').toString();
      case 'contract_count':
        return Number(customer.contract_count || 0);
      default:
        return '';
    }
  };

  const sortedCustomers = (() => {
    const list = [...customers];
    if (!sort?.key) return list;

    const dir = sort.dir === 'desc' ? -1 : 1;
    return list.sort((a, b) => {
      const av = getSortValue(a, sort.key);
      const bv = getSortValue(b, sort.key);

      // numeric sort for contract_count
      if (sort.key === 'contract_count') {
        return (av - bv) * dir;
      }

      // Special-case: Email sorting should be alphabetic, but empty emails go last.
      if (sort.key === 'email') {
        const aEmpty = !String(av || '').trim();
        const bEmpty = !String(bv || '').trim();
        if (aEmpty !== bEmpty) return aEmpty ? 1 : -1; // always push empty to bottom
        if (aEmpty && bEmpty) return 0;
        return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
      }

      return av.localeCompare(bv, undefined, { sensitivity: 'base' }) * dir;
    });
  })();

  const totalPages = Math.ceil(totalCustomers / itemsPerPage);
  const currentCustomers = sortedCustomers;

  const goToPage = async (page) => {
    const next = Math.max(1, Math.min(totalPages || 1, Number(page) || 1));
    setCurrentPage(next);
    await fetchData(next, itemsPerPage, searchQuery);
  };

  const SortIcon = ({ colKey }) => {
    const active = sort?.key === colKey;
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          "h-6 w-6 text-muted-foreground hover:text-foreground",
          active && "text-foreground dark:text-white",
          "dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/10"
        )}
        onClick={() => handleSort(colKey)}
        aria-label={`Sort ${colKey} ${active ? sort.dir : ''}`.trim()}
      >
        <ArrowUpDown className="h-3.5 w-3.5" />
      </Button>
    );
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="customers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight dark:text-white">{t('customers.title')}</h1>
            <p className="text-muted-foreground dark:text-gray-400 mt-1">
              {t('customers.totalCount', { count: totalCustomers })}
            </p>
          </div>
          <Button onClick={() => handleOpenModal()} data-testid="add-customer-btn" className="dark:bg-[#444444] dark:hover:bg-[#555555] dark:text-white">
            <Plus className="w-4 h-4 mr-2" />
            {t('customers.addNew')}
          </Button>
        </div>

        {/* İstatistik Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t('customers.stats.total'), value: customerStats?.total_customers ?? 0, icon: Users },
            { label: t('customers.stats.withActiveContract'), value: customerStats?.with_active_contract ?? 0, icon: FileCheck },
            { label: t('customers.stats.withoutContract'), value: customerStats?.without_contract ?? 0, icon: UserX },
            { label: t('customers.stats.newThisMonth'), value: customerStats?.new_this_month ?? 0, icon: UserPlus },
          ].map((stat, index) => (
            <Card
              key={index}
              className="stat-card relative overflow-hidden"
              style={{ backgroundColor: 'var(--theme-accent)' }}
              data-testid={`customer-stat-card-${index}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className="rounded-md bg-gray-700/60 p-1">
                    <stat.icon className="h-5 w-5 text-gray-300" />
                  </div>
                  <p className="label-uppercase text-white">{stat.label}</p>
                </div>
                <p className="mt-1 text-xl font-bold font-mono text-white">
                  {customerStats == null ? '—' : stat.value}
                </p>
                <stat.icon className="pointer-events-none absolute right-0 top-0 h-24 w-24 text-white/20" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* İstatistik Dağılımı — Firmaya & Sözleşme tipine göre (aramadan bağımsız, tam veri) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DistributionCard
            icon={Building2}
            title={t('customers.stats.byCompany')}
            footnote={t('customers.stats.activeContractsNote')}
            emptyText={t('customers.stats.empty')}
            formatMore={(n) => t('customers.stats.showAll', { count: n })}
            lessLabel={t('customers.stats.showLess')}
            items={companies.map((c) => ({
              id: c.id,
              name: c.name,
              count: c.active_contract_count || 0,
              inactive: c.status === false || c.status === 0,
            }))}
          />
          <DistributionCard
            icon={Bookmark}
            title={t('customers.stats.byType')}
            footnote={t('customers.stats.activeContractsNote')}
            emptyText={t('customers.stats.empty')}
            formatMore={(n) => t('customers.stats.showAll', { count: n })}
            lessLabel={t('customers.stats.showLess')}
            items={contractTypes.map((ct) => ({
              id: ct.id,
              name: ct.name,
              count: ct.active_contract_count || 0,
              inactive: ct.status === false || ct.status === 0,
            }))}
          />
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground dark:text-gray-400" />
          <Input
            placeholder={t('customers.search')}
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10 dark:bg-[#333333] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
            data-testid="customer-search-input"
          />
        </div>

        {/* Table */}
        <Card className="dark:bg-[#333333] dark:border-zinc-800 overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <Table className="w-full table-fixed">
              <TableHeader>
                <TableRow className="table-header dark:bg-[#444444] dark:border-gray-700 dark:hover:bg-[#444444]">
                  <TableHead className="w-[110px] dark:text-white">
                    <div className="flex items-center gap-1">
                      <span>{t('customers.firstName')}</span>
                      <SortIcon colKey="first_name" />
                    </div>
                  </TableHead>
                  <TableHead className="w-[110px] dark:text-white">
                    <div className="flex items-center gap-1">
                      <span>{t('customers.lastName')}</span>
                      <SortIcon colKey="last_name" />
                    </div>
                  </TableHead>
                  <TableHead className="w-[190px]">
                    <div className="flex items-center gap-1">
                      <span>{t('customers.email')}</span>
                      <SortIcon colKey="email" />
                    </div>
                  </TableHead>
                  <TableHead className="w-[130px]">
                    <div className="flex items-center gap-1">
                      <span>{t('customers.phone')}</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[220px]">
                    <div className="flex items-center gap-1">
                      <span>{t('customers.address')}</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[120px] whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <span>{t('customer.postalCode', { defaultValue: 'Posta Kodu' })}</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[130px] whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <span>{t('settings.contractCount', { defaultValue: 'Sözleşme Sayısı' })}</span>
                      <SortIcon colKey="contract_count" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right w-[120px]">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">{t('customers.noCustomers')}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentCustomers.map((customer) => (
                    <TableRow key={customer.id} className="table-row dark:border-gray-700/50 dark:hover:bg-[#444444]" data-testid={`customer-row-${customer.id}`}>
                      <TableCell className="table-cell font-medium truncate max-w-[110px] dark:text-white">
                        {customer.first_name}
                      </TableCell>
                      <TableCell className="table-cell truncate max-w-[110px]">
                        {customer.last_name}
                      </TableCell>
                      <TableCell className="table-cell text-muted-foreground truncate max-w-[190px]">
                        {customer.email || '-'}
                      </TableCell>
                      <TableCell className="table-cell font-mono truncate max-w-[130px]">
                        {customer.phone || '-'}
                      </TableCell>
                      <TableCell className="table-cell truncate max-w-[220px]">
                        {customer.address || '-'}
                      </TableCell>
                      <TableCell className="table-cell truncate max-w-[120px]">
                        {customer.zip_code || '-'}
                      </TableCell>
                      <TableCell className="table-cell w-[130px]">
                        {customer.contract_count || 0}
                      </TableCell>

                      <TableCell className="table-cell text-right w-[120px]">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetail(customer)}
                            data-testid={`view-customer-${customer.id}`}
                            className="dark:hover:bg-[#444444] dark:text-white"
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenModal(customer)}
                            data-testid={`edit-customer-${customer.id}`}
                            className="dark:hover:bg-[#444444] dark:text-white"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(customer)}
                            data-testid={`delete-customer-${customer.id}`}
                            className="dark:hover:bg-red-950/30 dark:text-red-500"
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination (Contracts sayfası ile aynı UX) */}
            <div className="flex items-center justify-between px-4 py-3 border-t dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground dark:text-gray-400">{t('contracts.perPage')}</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(val) => {
                    const nextSize = Number(val);
                    setItemsPerPage(nextSize);
                    setCurrentPage(1);
                    fetchData(1, nextSize, searchQuery);
                  }}
                >
                  <SelectTrigger className="w-20 h-8 dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-[#333333] dark:border-gray-700 dark:text-white">
                    <SelectItem value="10" className="dark:focus:bg-[#444444] dark:focus:text-white">10</SelectItem>
                    <SelectItem value="25" className="dark:focus:bg-[#444444] dark:focus:text-white">25</SelectItem>
                    <SelectItem value="50" className="dark:focus:bg-[#444444] dark:focus:text-white">50</SelectItem>
                    <SelectItem value="100" className="dark:focus:bg-[#444444] dark:focus:text-white">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground dark:text-gray-400">{t('contracts.showRecords')}</span>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('contracts.prev')}
                </Button>

                {getPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 dark:text-gray-400">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="sm"
                      className={cn(
                        "w-8 h-8 p-0",
                        currentPage === page
                          ? "dark:bg-white dark:text-[#333333]"
                          : "dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]"
                      )}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </Button>
                  )
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]"
                >
                  {t('contracts.next')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>



        {/* Add/Edit Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          {/* Outer container clips rounded corners; scroll happens inside */}
          <DialogContent className="sm:max-w-lg p-0 max-h-[98vh] overflow-hidden dark:bg-[#333333] dark:border-none dark:text-white" data-testid="customer-modal">
            <div className="p-4 max-h-[98vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingCustomer ? t('common.edit') : t('customers.addNew')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
                {/* Hidden fields so zod can validate conditional requirements */}
                <input type="hidden" {...register('no_email')} />
                <input type="hidden" {...register('no_phone')} />
                <div className="space-y-1">
                  <Label htmlFor="first_name" className="dark:text-white">{t('customers.firstName')}</Label>
                  <Input
                    id="first_name"
                    {...register('first_name')}
                    data-testid="customer-firstname-input"
                    className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {errors.first_name && (
                    <p className="text-sm text-destructive">{errors.first_name.message}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="last_name" className="dark:text-white">{t('customers.lastName')}</Label>
                  <Input
                    id="last_name"
                    {...register('last_name')}
                    data-testid="customer-lastname-input"
                    className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {errors.last_name && (
                    <p className="text-sm text-destructive">{errors.last_name.message}</p>
                  )}
                </div>

                {/* Group email + phone to reduce gap between them */}
                <div className="space-y-0">
                  <div className="space-y-1">
                    <Label htmlFor="email" className="dark:text-white">{t('customers.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      data-testid="customer-email-input"
                      disabled={noEmail}
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-muted-foreground dark:text-gray-400">
                        {t('customers.noEmail', { defaultValue: 'Keine E-Mail' })}
                      </span>
                      <Switch
                        checked={noEmail}
                        onCheckedChange={(checked) => {
                          setNoEmail(checked);
                          setValue('no_email', checked, { shouldValidate: true });
                          if (checked) setValue('email', '');
                        }}
                        className="dark:data-[state=unchecked]:bg-[#444444] dark:data-[state=checked]:bg-zinc-200"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-sm text-destructive">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-1 -mt-2">
                    <Label htmlFor="phone" className="dark:text-white">{t('customers.phone')}</Label>
                    <Input
                      id="phone"
                      {...register('phone')}
                      data-testid="customer-phone-input"
                      inputMode="tel"
                      placeholder="+49 123 456789"
                      disabled={noPhone}
                      className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-muted-foreground dark:text-gray-400">
                        {t('customers.noPhone', { defaultValue: 'Keine Telefonnummer' })}
                      </span>
                      <Switch
                        checked={noPhone}
                        onCheckedChange={(checked) => {
                          setNoPhone(checked);
                          setValue('no_phone', checked, { shouldValidate: true });
                          if (checked) setValue('phone', '');
                        }}
                        className="dark:data-[state=unchecked]:bg-[#444444] dark:data-[state=checked]:bg-zinc-200"
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-sm text-destructive">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="birth_date" className="dark:text-white">{t('customer.birthDate', { defaultValue: 'Doğum Tarihi' })}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full justify-start text-left font-normal dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]",
                          !watchedBirthDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {watchedBirthDate ? (
                          format(new Date(watchedBirthDate), "PPP", { locale: currentLocale })
                        ) : (
                          <span>{t('customer.birthDate', { defaultValue: 'Doğum Tarihi' })}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 dark:bg-[#333333] dark:border-gray-700" align="start">
                      <Calendar
                        mode="single"
                        selected={watchedBirthDate ? new Date(watchedBirthDate) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            // Format as YYYY-MM-DD for consistency with backend/schema
                            setValue('birth_date', format(date, 'yyyy-MM-dd'), { shouldValidate: true });
                          }
                        }}
                        initialFocus
                        captionLayout="dropdown-buttons"
                        fromYear={1800}
                        toYear={new Date().getFullYear()}
                        disabled={{ after: new Date() }}
                        locale={currentLocale}
                        className="dark:bg-[#333333] dark:text-white"
                        classNames={{
                          day_selected: "bg-zinc-900 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200",
                          day_today: "bg-accent text-accent-foreground dark:bg-white/10",
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                  <input type="hidden" {...register('birth_date')} />
                  {errors.birth_date && (
                    <p className="text-sm text-destructive">{errors.birth_date.message}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="address" className="dark:text-white">{t('customers.address')}</Label>
                  <Textarea
                    id="address"
                    rows={2}
                    {...register('address')}
                    data-testid="customer-address-input"
                    className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {errors.address && (
                    <p className="text-sm text-destructive">{errors.address.message}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="zip_code" className="dark:text-white">{t('customer.postalCode', { defaultValue: 'Posta Kodu' })}</Label>
                  <Input
                    id="zip_code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="12345"
                    {...register('zip_code')}
                    className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500"
                  />
                  {errors.zip_code && (
                    <p className="text-sm text-destructive">{errors.zip_code.message}</p>
                  )}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleCloseModal} className="dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]">
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="customer-save-btn" className="min-w-[100px] dark:bg-zinc-700 dark:hover:bg-zinc-600 dark:text-white">
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      t('common.save')
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto dark:bg-[#333333] dark:border-none dark:text-white" data-testid="customer-detail-modal">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {selectedCustomer?.first_name} {selectedCustomer?.last_name}
              </DialogTitle>
            </DialogHeader>

            {selectedCustomer && (
              <div className="space-y-6">
                {/* Customer Information */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground dark:text-gray-400 mb-2">
                    {t('customers.detailCustomerInfoTitle', { defaultValue: 'Müşteri Bilgileri' })}
                  </h3>
                  <div className="rounded-md border dark:border-gray-700 overflow-hidden">
                    <div className="grid grid-cols-3 text-sm">
                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">
                        {t('customers.detailCustomerLabel', { defaultValue: 'Müşteri:' })}
                      </div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700">
                        {selectedCustomer.first_name} {selectedCustomer.last_name}
                      </div>

                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">E-mail:</div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700">{selectedCustomer.email || '-'}</div>

                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">
                        {t('customers.phone', { defaultValue: 'Telefon' })}:
                      </div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700 font-mono">{selectedCustomer.phone || '-'}</div>

                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">
                        {t('customer.birthDate', { defaultValue: 'Doğum Tarihi' })}:
                      </div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700">
                        {formatDateDisplay(selectedCustomer.birth_date)}
                      </div>

                      <div className="col-span-1 px-4 py-3 border-b dark:border-gray-700 font-medium dark:bg-white/5">
                        {t('customer.postalCode', { defaultValue: 'Posta Kodu' })}:
                      </div>
                      <div className="col-span-2 px-4 py-3 border-b dark:border-gray-700">
                        {(selectedCustomer.zip_code || '-')}{selectedCustomer.city ? ` ${selectedCustomer.city}` : ''}
                      </div>

                      <div className="col-span-1 px-4 py-3 font-medium dark:bg-white/5">
                        {t('customers.address', { defaultValue: 'Adres' })}:
                      </div>
                      <div className="col-span-2 px-4 py-3">
                        {selectedCustomer.address || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contracts */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                    {t('customers.detailContractsTitle', { defaultValue: 'Sözleşmeler' })}
                  </h3>
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader className="bg-muted/50 dark:bg-[#444444] dark:border-gray-700">
                        <TableRow>
                          <TableHead className="w-[140px] dark:text-white">
                            {t('contracts.contractNo', { defaultValue: 'Sözleşme No' })}
                          </TableHead>
                          <TableHead className="dark:text-white">{t('contracts.company', { defaultValue: 'Şirket' })}</TableHead>
                          <TableHead className="dark:text-white">{t('contracts.type', { defaultValue: 'Sözleşme Türü' })}</TableHead>
                          <TableHead className="w-[160px] dark:text-white">
                            {t('customers.detailDaysLeft', { defaultValue: 'Bitime Kalan Gün' })}
                          </TableHead>
                          <TableHead className="text-right w-[140px] dark:text-white">
                            {t('common.actions', { defaultValue: 'İşlem' })}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customerContracts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                              {t('customers.detailNoContracts', { defaultValue: 'Sözleşme bulunamadı' })}
                            </TableCell>
                          </TableRow>
                        ) : (
                          customerContracts.map((contract) => (
                            <TableRow key={contract.id} className="table-row dark:border-gray-700/50 dark:hover:bg-[#444444]">
                              <TableCell className="font-medium">
                                {contract.contract_number || '-'}
                              </TableCell>
                              <TableCell className="truncate max-w-[220px]">
                                {contract.company_name || '-'}
                              </TableCell>
                              <TableCell className="truncate max-w-[220px]">
                                {contract.contract_type_name || '-'}
                              </TableCell>
                              <TableCell>
                                {contract.days_remaining !== null && contract.days_remaining !== undefined ? (
                                  <span className={contract.days_remaining < 30 ? "text-red-500 font-semibold" : ""}>
                                    {contract.days_remaining} {t('contracts.days', { defaultValue: 'Gün' })}
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleViewContractDetail(contract)}
                                    title={t('dashboard.detail', { defaultValue: 'Detay' })}
                                    className="dark:hover:bg-[#444444] dark:text-white"
                                  >
                                    <Info className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleEditContract(contract)}
                                    title={t('common.edit', { defaultValue: 'Düzenle' })}
                                    className="dark:hover:bg-[#444444] dark:text-white"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="sm:max-w-md dark:bg-[#333333] dark:border-none dark:text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-bold dark:text-white">
                {t('customers.deleteTitle')}
              </DialogTitle>
            </DialogHeader>

            <div className="py-4">
              <p className="text-gray-700 dark:text-gray-300">
                {t('customers.deleteConfirmIntro')} <span className="text-red-500 font-bold">{customerToDelete?.first_name} {customerToDelete?.last_name}</span> {t('customers.deleteConfirmWith')} <span className="text-red-500 font-bold underline">{customerToDelete?.contract_count || 0} {t('customers.deleteConfirmContracts')}</span>
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="destructive"
                className="bg-red-600 hover:bg-red-700 dark:bg-red-900 dark:hover:bg-red-800 text-white"
                onClick={handleDeleteConfirm}
              >
                {t('common.delete')}
              </Button>
              <Button
                variant="secondary"
                className="bg-gray-500 hover:bg-gray-600 dark:bg-[#444444] dark:hover:bg-[#555555] text-white"
                onClick={() => setDeleteModalOpen(false)}
              >
                {t('common.cancel')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contract Detail Modal (same as Contracts page) */}
        <Dialog open={isContractDetailOpen} onOpenChange={setIsContractDetailOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] p-0 overflow-hidden flex flex-col dark:bg-[#333333] dark:border-gray-700 dark:text-white" data-testid="contract-detail-modal">
            <DialogHeader className="px-6 py-4 border-b dark:border-gray-700">
              <DialogTitle className="text-lg font-normal dark:text-white">
                {t('contracts.detailTitle', { defaultValue: 'Sözleşme Detayı' })}
              </DialogTitle>
            </DialogHeader>

            {selectedContract && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-thin">
                  <div className="space-y-2">
                    <div className="flex flex-col">
                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm">{t('contracts.customer', { defaultValue: 'Müşteri' })}:</span>
                        <div className="flex items-center gap-1">
                          <span className="text-sm">{selectedContract.customer_name || '-'}</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm">{t('contracts.type', { defaultValue: 'Sözleşme Türü' })}:</span>
                        <span className="text-sm">{selectedContract.contract_type_name || '-'}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm">{t('contracts.company', { defaultValue: 'Şirket' })}:</span>
                        <span className="text-sm">{selectedContract.company_name || '-'}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm">{t('contracts.startDate', { defaultValue: 'Başlangıç Tarihi' })}:</span>
                        <span className="text-sm">{formatDateDisplay(selectedContract.start_date)}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm">{t('contracts.endDate', { defaultValue: 'Bitiş Tarihi' })}:</span>
                        <span className="text-sm">{formatDateDisplay(selectedContract.end_date)}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm">{t('contracts.daysRemaining', { defaultValue: 'Kalan Gün' })}:</span>
                        <span className="text-sm">{selectedContract.days_remaining} {t('contracts.days', { defaultValue: 'gün' })}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm">{t('contracts.orderNo', { defaultValue: 'Sipariş No' })}:</span>
                        <span className="text-sm">{selectedContract.order_number || '-'}</span>
                      </div>

                      <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm">{t('contracts.contractNo', { defaultValue: 'Sözleşme No' })}:</span>
                        <span className="text-sm">{selectedContract.contract_number || '-'}</span>
                      </div>

                      <div className="flex flex-col py-1 border-b border-gray-50 dark:border-zinc-800">
                        <span className="font-semibold text-sm mb-1">{t('contracts.description', { defaultValue: 'Açıklama' })}:</span>
                        <span className="text-sm">{selectedContract.description || '-'}</span>
                      </div>
                    </div>

                    <div className="border rounded-lg p-3 bg-muted/20 dark:bg-zinc-800/50 dark:border-zinc-800 mt-2">
                      <h3 className="font-semibold mb-1 text-sm">{t('contracts.history', { defaultValue: 'Sözleşme Geçmişi' })}:</h3>
                      <p className="text-xs text-gray-500">{t('contracts.noHistory', { defaultValue: 'Sözleşmeye ait uzatma bilgisi bulunamadı.' })}</p>
                    </div>

                    <div className="border rounded-lg p-3 bg-muted/20 dark:bg-zinc-800/50 dark:border-zinc-800 mt-2">
                      <h3 className="font-semibold mb-1 text-sm">{t('contracts.contractFiles', { defaultValue: 'Sözleşme Dosyaları' })}:</h3>
                      <div className="grid grid-cols-1 gap-2 mt-2">
                        {selectedContract.files && selectedContract.files.length > 0 ? (
                          selectedContract.files.map((file, index) => (
                            <div
                              key={index}
                              className="border rounded-lg p-4 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors bg-white dark:bg-zinc-800/50 dark:border-zinc-700"
                              onClick={async () => {
                                if (file.url) {
                                  try {
                                    const response = await contractsAPI.downloadFile(file.url);
                                    const contentType = response.headers['content-type'] || 'application/octet-stream';
                                    const blob = new Blob([response.data], { type: contentType });
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    window.open(blobUrl, '_blank');
                                  } catch (error) {
                                    console.error('File download error:', error);
                                    toast.error(t('contracts.fileOpenError', { defaultValue: 'Error opening file' }));
                                  }
                                } else {
                                  toast.info(t('contracts.noFileUrl', { defaultValue: 'File not available' }));
                                }
                              }}
                            >
                              <div className="w-12 h-16 border-2 border-gray-300 dark:border-zinc-600 rounded flex items-center justify-center bg-white dark:bg-zinc-800">
                                <FileText className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                              </div>
                              <div className="text-center">
                                <p className="font-bold text-sm truncate max-w-[200px]">{file.name}</p>
                                <p className="text-xs text-gray-500">
                                  {file.size ? (parseInt(file.size) / 1024 / 1024).toFixed(2) : '0.16'} MB
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-400 py-4 text-center">
                            {t('contracts.noFilesUploaded', { defaultValue: 'No files uploaded' })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-6 pt-4 border-t dark:border-gray-700 flex justify-end bg-white dark:bg-[#333333]">
                  <Button
                    variant="outline"
                    onClick={() => setIsContractDetailOpen(false)}
                    className="bg-gray-500 hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 text-white border-0"
                  >
                    {t('contracts.close', { defaultValue: 'Kapat' })}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Contract Edit Modal (same as Contracts page) */}
        <Dialog open={isContractEditOpen} onOpenChange={setIsContractEditOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] p-0 overflow-hidden flex flex-col dark:bg-[#333333] dark:border-gray-700 dark:text-white" data-testid="contract-edit-modal">
            <DialogHeader className="px-6 py-4 border-b dark:border-gray-700">
              <DialogTitle className="text-lg font-normal dark:text-white">
                {t('contracts.editTitle', { defaultValue: 'Müşteri Bilgilerini Güncelle' })}
              </DialogTitle>
            </DialogHeader>

            {editingContract && (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-thin">
                  <div className="space-y-4">
                    {/* De-duplicate types by (company_id + name) */}
                    {(() => {
                      const uniqueContractTypes = Array.from(
                        new Map(
                          (contractTypes || []).map((ct) => [`${ct.company_id || ''}::${ct.name || ''}`, ct])
                        ).values()
                      );
                      return (
                        <div className="space-y-1.5">
                          <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.selectContractType', { defaultValue: 'Öncelikle Bir Sözleşme Türü Seçiniz' })}</Label>
                          <Select
                            value={editingContract.contract_type_id?.toString()}
                            onValueChange={(val) => setEditingContract({ ...editingContract, contract_type_id: val })}
                          >
                            <SelectTrigger className="h-9 dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                              <SelectValue placeholder={t('contracts.type', { defaultValue: 'Sözleşme Türü' })} />
                            </SelectTrigger>
                            <SelectContent className="dark:bg-[#333333] dark:border-gray-700 dark:text-white">
                              {uniqueContractTypes.map((ct) => (
                                <SelectItem key={ct.id} value={ct.id.toString()} className="dark:focus:bg-[#444444] dark:focus:text-white">{ct.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })()}

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.selectCompanyLabel', { defaultValue: 'Şirket Seç' })}</Label>
                      <Select
                        value={editingContract.company_id?.toString()}
                        onValueChange={(val) => setEditingContract({ ...editingContract, company_id: val })}
                      >
                        <SelectTrigger className="h-9 dark:bg-[#444444] dark:border-gray-700 dark:text-white">
                          <SelectValue placeholder={t('contracts.company', { defaultValue: 'Şirket' })} />
                        </SelectTrigger>
                        <SelectContent className="dark:bg-[#333333] dark:border-gray-700 dark:text-white">
                          {companies.map((c) => (
                            <SelectItem key={c.id} value={c.id.toString()} className="dark:focus:bg-[#444444] dark:focus:text-white">{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.startDate', { defaultValue: 'Başlangıç Tarihi' })}</Label>
                      <Input
                        type="date"
                        value={editingContract.start_date ? new Date(editingContract.start_date).toISOString().split('T')[0] : ''}
                        onChange={(e) => setEditingContract({ ...editingContract, start_date: e.target.value })}
                        className="h-9 dark:bg-[#444444] dark:border-gray-700 dark:text-white"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.endDate', { defaultValue: 'Bitiş Tarihi' })}</Label>
                      <Input
                        type="date"
                        value={editingContract.end_date ? new Date(editingContract.end_date).toISOString().split('T')[0] : ''}
                        onChange={(e) => setEditingContract({ ...editingContract, end_date: e.target.value })}
                        className="h-9 dark:bg-[#444444] dark:border-gray-700 dark:text-white"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.extendDate', { defaultValue: 'Sözleşme Bitiş Tarihini Uzat' })}</Label>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="rounded-full h-7 text-xs dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]" onClick={() => handleExtendContractDate(6)}>
                          {t('contracts.6months', { defaultValue: '+6 Ay' })}
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full h-7 text-xs dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]" onClick={() => handleExtendContractDate(12)}>
                          {t('contracts.1year', { defaultValue: '+1 Yıl' })}
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full h-7 text-xs dark:bg-[#444444] dark:border-gray-700 dark:text-white dark:hover:bg-[#555555]" onClick={() => handleExtendContractDate(24)}>
                          {t('contracts.2years', { defaultValue: '+2 Yıl' })}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.orderNo', { defaultValue: 'Sipariş No' })}</Label>
                      <Input
                        value={editingContract.order_number || ''}
                        onChange={(e) => setEditingContract({ ...editingContract, order_number: e.target.value })}
                        className="h-9 dark:bg-[#333333] dark:border-gray-700 dark:text-gray-400"
                        readOnly
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">{t('contracts.contractNo', { defaultValue: 'Sözleşme No' })}</Label>
                      <Input
                        placeholder={t('contracts.contractNoPlaceholder', { defaultValue: 'Sözleşme No Girilmemiş' })}
                        value={editingContract.contract_number || ''}
                        onChange={(e) => setEditingContract({ ...editingContract, contract_number: e.target.value })}
                        className="h-9 dark:bg-[#444444] dark:border-gray-700 dark:text-white"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-700">{t('contracts.descriptionOptional', { defaultValue: 'Açıklama(Opsiyonel)' })}</Label>
                      <textarea
                        className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] dark:border-gray-700 dark:text-white"
                        value={editingContract.description || ''}
                        onChange={(e) => setEditingContract({ ...editingContract, description: e.target.value })}
                      />
                    </div>

                    <div className="space-y-4 pt-2">
                      <div className="flex flex-col gap-4">
                        <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {t('contracts.contractFiles', { defaultValue: 'Sözleşme Dosyaları' })}
                        </Label>

                        <div className="space-y-3">
                          {/* Show Existing Files */}
                          {editingContract.files && editingContract.files.map((file, idx) => (
                            <div key={`existing-${idx}`} className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center justify-between bg-white dark:bg-[#444444]/30 shadow-sm">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-16 border-2 border-gray-100 dark:border-gray-600 rounded-lg flex items-center justify-center bg-white dark:bg-[#444444] shadow-sm">
                                  <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-[250px]">{file.name}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {file.size ? (parseInt(file.size) / 1024 / 1024).toFixed(2) : '0.16'} MB
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteExistingContractFile(file.id);
                                }}
                              >
                                <X className="w-5 h-5" />
                              </Button>
                            </div>
                          ))}

                          {/* Show Newly Selected Files */}
                          {selectedFiles.map((file, idx) => (
                            <div key={`new-${idx}`} className="border-2 border-dashed border-blue-200 dark:border-blue-900/30 rounded-xl p-4 flex items-center justify-between bg-blue-50/30 dark:bg-blue-900/10 shadow-sm group">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-16 border-2 border-blue-100 dark:border-blue-800 rounded-lg flex items-center justify-center bg-white dark:bg-[#444444] shadow-sm">
                                  <span className="text-blue-600 dark:text-blue-400 font-bold text-xs uppercase">New</span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-[250px]">{file.name}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
                                }}
                              >
                                <X className="w-5 h-5" />
                              </Button>
                            </div>
                          ))}

                          {/* Upload Area - Always shown to allow adding more files */}
                          <div
                            className={cn(
                              "relative border-2 border-dashed rounded-lg p-12 text-center flex flex-col items-center justify-center gap-4 cursor-pointer transition-all",
                              dragActive
                                ? "border-blue-500 bg-blue-50/30 dark:border-blue-400 dark:bg-blue-400/10 scale-[1.01]"
                                : "border-green-700/50 bg-green-50/20 dark:bg-white/5 dark:border-gray-700"
                            )}
                            onClick={() => fileInputRef.current?.click()}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                          >
                            <input
                              type="file"
                              ref={fileInputRef}
                              className="hidden"
                              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                              multiple
                              onChange={handleContractFileChange}
                            />
                            <UploadCloud className={cn("w-12 h-12 transition-colors", dragActive ? "text-blue-500 dark:text-blue-400" : "text-green-700 dark:text-gray-400")} />
                            <div>
                              <p className="font-semibold text-zinc-800 dark:text-white text-lg">{t('contract.dragDrop')}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mt-1">{t('contract.supportedFormats')}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('contract.maxSize')}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 pt-4 border-t dark:border-gray-700 flex justify-end gap-2 bg-white dark:bg-[#333333]">
                  <Button
                    variant="outline"
                    onClick={() => setIsContractEditOpen(false)}
                    className="bg-gray-500 hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 text-white border-0"
                  >
                    {t('contracts.close', { defaultValue: 'Kapat' })}
                  </Button>
                  <Button
                    className="bg-zinc-800 hover:bg-zinc-700 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-white"
                    onClick={handleSaveContractChanges}
                  >
                    {t('contracts.update', { defaultValue: 'Güncelle' })}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
